// Display formatting shared by every page. All helpers accept null/undefined
// (and money accepts the API's decimal strings, e.g. costCents: "2.0000") and
// fall back to an em dash rather than rendering "NaN" or "Invalid Date".

const EMPTY = '—'

function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatMoney(cents: number | string | null | undefined): string {
  const n = toNumber(cents)
  if (n == null) return EMPTY
  const format = (value: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value)
  // Costs are recorded in US cents (providers price in dollars).
  // Fractions of a cent (a subscription's notional per-run price) would read as $0.00.
  if (n > 0 && n < 1) return `< ${format(0.01)}`
  return format(n / 100)
}

/** Shown next to a subscription run's cost, wherever it's rendered. */
export const SUBSCRIPTION_COST_TOOLTIP =
  "Estimated at API prices; your subscription isn't charged per run"

/**
 * A run's cost, honoring the workspace's "Run costs" setting for a
 * subscription login (Run.billing === 'subscription'): nothing is actually
 * billed per run, so `costCents` is a notional, API-price estimate. An
 * ordinary API-key run (or an older run with no `billing` recorded) just
 * shows its cost as money, same as before.
 */
export function formatRunCost(
  run: { costCents: number | string | null | undefined; billing?: 'subscription' | 'api' | null },
  runCostDisplay: 'estimate' | 'included' = 'estimate'
): { text: string; tooltip?: string } {
  if (run.billing !== 'subscription') return { text: formatMoney(run.costCents) }
  if (runCostDisplay === 'included') {
    return { text: 'Included', tooltip: SUBSCRIPTION_COST_TOOLTIP }
  }
  const amount = formatMoney(run.costCents)
  return { text: amount === EMPTY ? amount : `~${amount}`, tooltip: SUBSCRIPTION_COST_TOOLTIP }
}

export function formatNumber(
  value: number | string | null | undefined
): string {
  const n = toNumber(value)
  return n == null ? EMPTY : n.toLocaleString()
}

export function formatDateTime(value: string | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function formatTime(value: string | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

/** "3 minutes ago", "in 12 minutes", "yesterday". */
export function formatRelative(
  value: string | null | undefined,
  now: number = Date.now()
): string {
  const d = toDate(value)
  if (!d) return EMPTY
  const seconds = Math.round((d.getTime() - now) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 45) return seconds <= 0 ? 'just now' : 'in a moment'
  if (abs < 3600) return RELATIVE.format(Math.round(seconds / 60), 'minute')
  if (abs < 86400) return RELATIVE.format(Math.round(seconds / 3600), 'hour')
  if (abs < 86400 * 30)
    return RELATIVE.format(Math.round(seconds / 86400), 'day')
  return formatDateTime(value)
}

export function formatDuration(
  startedAt: string | null | undefined,
  endedAt?: string | null
): string {
  const start = toDate(startedAt)
  if (!start) return EMPTY
  const end = toDate(endedAt ?? null)?.getTime() ?? Date.now()
  return formatMs(end - start.getTime())
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return EMPTY
  const seconds = Math.max(0, Math.round(ms / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** First 8 characters of a UUID — enough to tell rows apart, short enough to scan. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : EMPTY
}

/** "plane_webhook" -> "Plane webhook". */
export function humanize(value: string | null | undefined): string {
  if (!value) return EMPTY
  const text = value.replace(/[_-]+/g, ' ').trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}
